/**
 * Returns a function that as long as it continues to be invoked, won't be triggered.
 * @param {Function} fn - Callback function.
 * @param {number} [wait=300] - Delay (in milliseconds).
 * @returns {Function}
 */
function debounce(fn, wait = 300) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

if (!customElements.get('announcement-bar')) {
  class AnnouncementBar extends HTMLElement {
    constructor() {
      super();
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.menu = document.querySelector('.main-menu__content');
      this.isPaused = false;

      this.bindEvents();
      this.init();
    }

    disconnectedCallback() {
      if (this.marqueeObserver) this.marqueeObserver.disconnect();

      if (this.moveLinksHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.moveLocalizationHandler) {
        window.removeEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }
    }

    bindEvents() {
      if (this.links) {
        this.moveLinksHandler = this.moveLinksHandler || this.moveLinks.bind(this);
        window.addEventListener('on:breakpoint-change', this.moveLinksHandler);
      }

      if (this.localization) {
        this.moveLocalizationHandler = this.moveLocalizationHandler
          || debounce(this.moveLocalization.bind(this));
        window.addEventListener('on:breakpoint-change', this.moveLocalizationHandler);
      }

      if (this.slider) {
        this.slider.addEventListener('mouseenter', this.pauseSlider.bind(this));
        this.slider.addEventListener('mouseleave', this.resumeSlider.bind(this));
      }
    }

    /**
     * Start sliders a slidin'
     */
    init() {
      if (this.slider) {
        const slides = this.slider.querySelectorAll('.announcement__text');
        slides[0].classList.add('is-visible');

        const nextSlide = () => {
          if (!this.isPaused && theme.elementUtil.isInViewport(this)) {
            const currSlide = this.slider.querySelector('.announcement__text.is-visible');
            const currSlideIndex = Array.from(currSlide.parentNode.children)
              .indexOf(currSlide);
            const nextSlideIndex = currSlideIndex + 1 < slides.length ? currSlideIndex + 1 : 0;

            if (document.dir === 'rtl') {
              this.slider.scrollTo(
                -Math.abs(nextSlideIndex * slides[nextSlideIndex].clientWidth),
                0
              );
            } else {
              this.slider.scrollTo(slides[nextSlideIndex].offsetLeft - 32, 0);
            }

            slides[nextSlideIndex].classList.add('is-visible');
            slides[currSlideIndex].classList.remove('is-visible');
          }

          setTimeout(nextSlide, this.dataset.slideDelay);
        };
        setTimeout(nextSlide, this.dataset.slideDelay);
      }

      this.initMarquee();

      if (this.links) {
        this.moveLinks();
      }

      if (this.localization) {
        this.moveLocalization();
      }
    }

    /**
     * Marquee mode: duration = width of one group of announcements / speed (px per second),
     * so the scroll speed stays the same whatever the text length or screen size.
     */
    initMarquee() {
      const marquee = this.querySelector('[data-marquee]');
      if (!marquee) return;

      const track = marquee.querySelector('.announcement__marquee-track');
      const group = marquee.querySelector('.announcement__marquee-group');
      if (!track || !group) return;

      const speed = parseFloat(marquee.dataset.marqueeSpeed) || 50;
      const update = () => {
        const width = group.getBoundingClientRect().width;
        if (width > 0) {
          track.style.setProperty('--marquee-duration', `${Math.max(width / speed, 3).toFixed(2)}s`);
        }
      };

      update();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(update);
      if ('ResizeObserver' in window) {
        this.marqueeObserver = new ResizeObserver(update);
        this.marqueeObserver.observe(group);
      } else {
        window.addEventListener('resize', update);
      }
    }

    /**
     * Move the top bar links (Store Locator, Help) to the mobile nav drawer,
     * or remove them when switching back to desktop.
     *
     * Respects the data-show-in-mobile attribute set from the customizer toggle.
     */
    moveLinks() {
      const showInMobile = this.dataset.showInMobile !== 'false';
      const menuAnnouncementLinks = document.querySelector('.mob__announcement-links');

      if (!theme.mediaMatches.md) {
        // Mobile breakpoint
        if (!showInMobile) {
          // User disabled mobile links in customizer — remove if present
          if (menuAnnouncementLinks) menuAnnouncementLinks.remove();
          return;
        }

        if (!menuAnnouncementLinks) {
          // Build the mobile links nav
          const mobNav = document.createElement('nav');
          mobNav.classList.add('mob__announcement-links');

          const mobNavUl = document.createElement('ul');
          mobNavUl.classList.add('secondary-nav');
          mobNavUl.setAttribute('role', 'list');

          this.links.forEach((link) => {
            const li = document.createElement('li');
            const a = document.createElement('a');
            a.classList.add('secondary-nav__item');
            a.href = link.href;
            // Use data-link-label if available, else fall back to innerText
            a.textContent = link.dataset.linkLabel || link.innerText.trim();
            li.appendChild(a);
            mobNavUl.appendChild(li);
          });

          mobNav.appendChild(mobNavUl);
          // Append at the very bottom of the mobile menu content
          this.menu.appendChild(mobNav);
        }
      } else if (theme.mediaMatches.md && menuAnnouncementLinks) {
        // Desktop breakpoint — remove mobile links
        menuAnnouncementLinks.remove();
      }
    }

    /**
     * Move the localization selectors to the mobile nav/back to the announcement bar if necessary
     */
    moveLocalization() {
      this.slider = this.querySelector('.announcement__slider');
      this.localization = this.querySelector('.announcement__localization');
      this.links = this.querySelectorAll('.js-announcement-link');
      this.menu = document.querySelector('.main-menu__content');
      const menuLocalization = document.querySelector('.mob__localization');
      if (!theme.mediaMatches.md && !menuLocalization) {
        // Move localization to mobile
        const mobLocalizationElem = document.createElement('div');
        mobLocalizationElem.classList.add('mob__localization');
        mobLocalizationElem.appendChild(this.localization.firstElementChild);
        this.menu.appendChild(mobLocalizationElem);
      } else if (theme.mediaMatches.md && menuLocalization) {
        // Move localization back to announcement bar
        this.localization.appendChild(menuLocalization.firstElementChild);
        menuLocalization.remove();
      }
    }

    /**
     * Pauses the auto-rotating slider
     */
    pauseSlider() {
      this.isPaused = true;
    }

    /**
     * Resumes the auto-rotating slider
     */
    resumeSlider() {
      this.isPaused = false;
    }
  }

  customElements.define('announcement-bar', AnnouncementBar);
}
